/** @jsx isolateComponent */

import type { ReactNode } from 'react';
import type { Interpolation } from '@emotion/css';

/* eslint-disable import/no-duplicates */
import type { IconName } from '@zapier/design-system';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Tooltip,
  Typography,
} from '@zapier/design-system';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

export type Props = {
  /**
   * The URL to where the link goes.
   */
  href: string | null;
  /**
   * The `Icon` component to render within the link.
   * **Note that if renderBeforeText is defined, that prop is then responsible for rendering.**
   */
  icon?: IconName;
  /**
   * Indicates whether the link is collapsed down to just an icon.
   */
  isCollapsed: boolean;
  /**
   * Indicates whether the link goes to a page outside of
   * the logged in app experience.
   */
  isExternal: boolean;
  /**
   * Indicates whether the link is the selected/current link.
   */
  isSelected: boolean;
  /**
   * Optional click handler for the link.
   */
  onClick?: (e: MouseEvent) => void;
  /**
   * Renders content before the link text. E.g; an Icon or Avatar
   */
  renderBeforeText?: (props: Props) => ReactNode;
  /**
   * The `role` attribute of the `UniversalNavLink`, which specifies
   * the function of the nested `Link`.
   */
  role?: string;
  /**
   * The text content to render within the link.
   */
  text: string;
  /**
   * Optional target for the link.
   **/
  target?: string;
};

const tooltipClassName = createUniqueClassName('UniversalNavLink', 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;
const externalIconClassName = createUniqueClassName(
  'UniversalNavLink',
  'externalIcon'
);
const externalIconSelector = `.${externalIconClassName}`;

const Styles = labelStyles('UniversalNavLink', {
  root: (props: Props, { hasBeforeText }: { hasBeforeText: boolean }) => [
    // Rare specificity bump over `Link`.
    withModifier('', {
      ...Typography.base,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '20px',
      color: Colors.gray80,
      display: 'flex',
      position: 'relative',
      minHeight: 50,
      padding: 10,
      borderRadius: 10,
      whiteSpace: 'nowrap',
      width: '100%',
      transition: Animation.transitionValue,
      textDecoration: 'none',

      '&:hover, &:focus': {
        color: Colors.gray,
        backgroundColor: Colors.gray10,
        outline: 'none',
      },
    }),
    !hasBeforeText &&
      withModifier('textOnly', {
        paddingLeft: 15,
        paddingRight: 15,
      }),
    props.isSelected &&
      withModifier('selected', {
        color: Colors.ocean,
        backgroundColor: Colors.ocean5,
      }),
    props.isCollapsed &&
      withModifier('collapsed', {
        width: 50,
      }),
    {
      [tooltipSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.1s ease-in-out',
        '@media (hover: none)': {
          display: 'none',
        },
      },
      [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    {
      [externalIconSelector]: {
        opacity: 0,
      },
      [`&:hover ${externalIconSelector}, &:focus ${externalIconSelector}`]: {
        opacity: 0.5,
      },
    },
  ],
  layout: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: 20,
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  text: {
    transition: Animation.transitionValue,
    '&[aria-hidden="true"]': {
      opacity: 0,
    },
  },
});

/**
 * @private
 * Renders a link within the universal sidebar.
 */
const UniversalNavLink = (props: Props) => {
  const tooltip = props.isCollapsed && (
    <span className={tooltipClassName}>
      <Tooltip position="east">{props.text}</Tooltip>
    </span>
  );
  const onClick = (e: MouseEvent) => {
    // Chrome is the only browser that causes an element to
    // be in a `:focus` state when it's clicked. That `:focus`
    // state causes `Tooltip` to undesirably render. The user
    // already clicked, so they don't need to see the tooltip.
    // To hide the tooltip, `blur` the node to cause it to no
    // longer have `:focus`. Tabbing location is still preserved.
    (e.currentTarget as HTMLElement).blur();
    if (props.onClick) {
      props.onClick(e);
    }
  };
  const beforeText = props.renderBeforeText && props.renderBeforeText(props);
  return (
    <Link
      color={null}
      css={Styles.root(props, { hasBeforeText: !!beforeText }) as Interpolation}
      href={props.href}
      onClick={onClick}
      role={props.role}
      target={props.target || '_self'}
    >
      <span css={Styles.layout as Interpolation}>
        {beforeText}
        <span aria-hidden={props.isCollapsed} css={Styles.text}>
          {props.text}
        </span>
        {props.isExternal && !props.isCollapsed && (
          <span className={externalIconClassName}>
            <Icon isBlock={true} name="arrowOffsite" size={18} />
          </span>
        )}
      </span>
      {tooltip}
    </Link>
  );
};

UniversalNavLink.defaultProps = {
  isCollapsed: false,
  isExternal: false,
  isSelected: false,
  renderBeforeText: (props: Props) =>
    props.icon && <Icon isBlock={true} name={props.icon} size={30} />,
};

export default UniversalNavLink;
